import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Search } from 'lucide-react';
import { adminUsers, PERMISSIONS, permissions } from '@ooh/contracts';
import type { UserAdminListItem } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ooh/ui/components/select';
import {
  SearchableSelectField,
  type SearchableSelectFieldOption,
} from '@ooh/ui/components/searchable-select-field';
import { useCurrentUser, useHasPermission } from '../../plugs/current-user';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { PageLayout } from '../../layouts/page-layout';
import { CreateUserDialog } from './create-user-dialog';
import { EditUserDialog } from './edit-user-dialog';
import { UsersTable } from './users-table';

const DEFAULT_PAGE_SIZE = 25;

export interface UsersSearch {
  page?: number;
  pageSize?: number;
  roleCode?: string;
  isActive?: boolean;
  search?: string;
}

interface UsersListPageProps {
  search: UsersSearch;
  onSearchChange: (patch: Partial<UsersSearch>) => void;
}

interface UsersListContentProps extends UsersListPageProps {
  onEdit: (user: UserAdminListItem) => void;
}

function statusFilterValue(isActive: boolean | undefined): string {
  if (isActive === undefined) return 'all';
  return isActive ? 'active' : 'inactive';
}

function UsersListContent({ search, onSearchChange, onEdit }: UsersListContentProps) {
  const { t } = useTranslation('admin');
  // The list is requested with the deferred filters so typing never swaps the page for its skeleton.
  const requested = useDeferredValue(search);
  const page = requested.page ?? 1;
  const pageSize = requested.pageSize ?? DEFAULT_PAGE_SIZE;

  const { data } = useApiSuspenseQuery(adminUsers.listUsers, {
    roleCode: requested.roleCode,
    isActive: requested.isActive,
    search: requested.search,
    page,
    pageSize,
  });
  const { data: roles } = useApiSuspenseQuery(permissions.getRoles, {});

  const roleFilterOptions: SearchableSelectFieldOption[] = [
    { value: 'all', label: t('users.filters.roleAll') },
    ...roles.map((r) => ({ value: r.code, label: r.name })),
  ];

  function setFilter(patch: Partial<UsersSearch>) {
    onSearchChange({ ...patch, page: 1 });
  }

  return (
    <>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('users.filters.searchPlaceholder')}
            value={search.search ?? ''}
            onChange={(e) => setFilter({ search: e.target.value || undefined })}
            className="pl-9"
          />
        </div>
        <SearchableSelectField
          className="w-[160px]"
          options={roleFilterOptions}
          value={search.roleCode ?? 'all'}
          onChange={(v) => setFilter({ roleCode: v === 'all' ? undefined : v })}
          searchPlaceholder={t('users.filters.roleSearchPlaceholder')}
          emptyText={t('users.filters.roleNoResults')}
          data-testid="users-role-filter"
        />
        <Select
          value={statusFilterValue(search.isActive)}
          onValueChange={(v) => setFilter({ isActive: v === 'all' ? undefined : v === 'active' })}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('users.filters.statusAll')}</SelectItem>
            <SelectItem value="active">{t('users.filters.statusActive')}</SelectItem>
            <SelectItem value="inactive">{t('users.filters.statusInactive')}</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-[13px] text-muted-foreground">{data.total}</span>
      </div>

      <div className={requested === search ? undefined : 'opacity-60 transition-opacity'}>
        <UsersTable
          items={data.items}
          total={data.total}
          page={page}
          pageSize={pageSize}
          onEdit={onEdit}
          onPageChange={(p) => onSearchChange({ page: p })}
        />
      </div>
    </>
  );
}

export function UsersListPage({ search, onSearchChange }: UsersListPageProps) {
  const { t } = useTranslation('admin');
  const canManageUsers = useHasPermission(PERMISSIONS.ADMIN_MANAGE_USERS);
  const { user: currentUser } = useCurrentUser();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserAdminListItem | null>(null);

  if (!canManageUsers) {
    return (
      <div
        data-testid="forbidden-message"
        className="flex h-full items-center justify-center py-20"
      >
        <p className="text-muted-foreground">{t('users.accessDenied')}</p>
      </div>
    );
  }

  return (
    <>
      <PageLayout
        title={t('users.pageTitle')}
        description={t('users.pageSubtitle')}
        icon={<Users className="h-5 w-5" />}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('users.actions.addUser')}
          </Button>
        }
      >
        <UsersListContent search={search} onSearchChange={onSearchChange} onEdit={setEditingUser} />
      </PageLayout>

      {createOpen ? (
        <CreateUserDialog
          onClose={() => setCreateOpen(false)}
          onSuccess={() => setCreateOpen(false)}
        />
      ) : null}
      {editingUser !== null ? (
        <EditUserDialog
          user={editingUser}
          currentUserId={currentUser?.id}
          onClose={() => setEditingUser(null)}
          onSuccess={() => setEditingUser(null)}
        />
      ) : null}
    </>
  );
}
